const en = {
  meta: {
    title: 'Agathit Domrongwatanagoon, CTO and full stack developer',
    description:
      'Agathit Domrongwatanagoon, CTO and full stack developer in Bangkok. Founder of FindBack and digital transformation team lead at Somchai Aluminium and SAG All Color.',
  },
  ui: {
    nav: { work: 'Work', career: 'Career', skills: 'Skills', about: 'About' },
    mainNav: 'Main',
    language: 'Language',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    emailMe: 'Email me',
    seeWork: 'See selected work',
    copyEmail: 'Copy email address',
    emailCopied: 'Email address copied',
    highlights: 'Highlights',
    filterLabel: 'Filter work by type',
    techUsed: 'Technologies used',
    visit: 'Visit',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    showAllProjects: (count) => `Show all ${count} projects`,
    showFewerProjects: 'Show fewer',
    headings: {
      work: 'Selected work',
      career: 'Career so far',
      skills: 'What I work with',
      about: 'Outside the day job',
      contact: 'Hiring, or have a project? Email me.',
    },
    education: 'Education',
    activities: 'Activities',
    interests: 'Interests',
    signal: {
      aria: 'A signal that starts as a smooth analog wave and turns into digital samples',
      analog: 'Analog signal',
      sampled: (bits, levels) => `Sampled at ${bits} bits, ${levels} levels`,
      hint: 'Move across the signal to shift the sampling point. Move up for more bits.',
    },
  },
  person: {
    location: 'Bangkok, Thailand',
    intro:
      'I turn manual business processes into software that staff use every day, and I keep it running in production. CTO of Software Marketplace, founder of FindBack, and digital transformation team lead at Somchai Aluminium and SAG All Color.',
  },
  proof: {
    lead: 'Western Digital adopted my contaminant-detection software and filed for a patent on it.',
    supporting: [
      'CTO and sole engineer of Software Marketplace, an early-stage startup, and founder of FindBack, a free lost-and-found platform for Thailand.',
      'Digital transformation team lead at Somchai Aluminium and SAG All Color, where I design, deploy and operate the internal systems.',
      'Earlier: software for Pizza Hut Thailand and NXP Semiconductors.',
    ],
  },
  filters: { all: 'All work', web: 'Web platforms', ai: 'AI and machine vision' },
  projects: {
    mesa: {
      title: 'MESA Density Particles Detection',
      place: 'Western Digital',
      summary:
        'Image-processing software that finds microscopic contaminants on hard-disk media. Western Digital adopted it and filed for a patent.',
      details:
        'Developed an improved algorithm for detecting contaminators on hard disk drives using advanced image processing techniques. Created a desktop application with an intuitive UI for operation by technicians. The solution was successfully adopted and filed for patent by Western Digital.',
    },
    'project-quotation': {
      title: 'Project and Quotation Management System',
      place: 'Somchai Aluminium',
      summary:
        'Runs the sales paperwork of an aluminium and glass business, from quotation to delivery note. Staff use it every day.',
      details:
        'Covers customers, quotations with multiple revisions, markup, commission and discounts, approvals, projects, invoices, payment records and delivery notes. Documents are rendered to A4 PDF on the server, and quotations are issued in Thai, Chinese and English. Access is role-based, with an audit log. I designed the system, deployed it and operate it in production.',
    },
    findback: {
      title: 'FindBack',
      place: 'Founder',
      summary:
        'A free platform for posting and finding lost and found items across Thailand, with a map of the listings.',
      details:
        'People post what they have lost or found, by category such as wallets, phones, keys, documents, pets and vehicles, then browse the listings on a map. It is free to use. I own and run the service.',
    },
    'self-hosted-platform': {
      title: 'Self-hosted Platform for Six Web Apps',
      place: 'Somchai Aluminium and SAG All Color',
      summary:
        'One Ubuntu server that runs six web applications in Docker behind Nginx and Cloudflare, with nightly database backups.',
      details:
        'Production, demo, database and reverse-proxy workloads each run in their own Docker network. Every deploy follows a written, repeatable procedure: back up the database and images, run the migrations, check the result, and keep a way to roll back. Web traffic reaches the server only through Cloudflare, the databases are not exposed to the internet, and office access goes through a WireGuard VPN.',
    },
    central: {
      title: 'Central Web Platform for Pizza Hut',
      place: 'PH Capital (Pizza Hut)',
      summary:
        'The internal operations platform for coupons, store management, employee records and customer data.',
      details:
        'Built and maintained the internal operations platform for coupons, store management, employee records, and customer data. Developed the frontend using React, and backend APIs using Express.js with an MSSQL database. Organized the backend into Controller / Router / Service layers.',
    },
    'lot-status': {
      title: 'Lot Status Tracking System',
      place: 'NXP Semiconductors',
      summary:
        'Tracks semiconductor lots through manufacturing, with real-time dashboards for production data.',
      details:
        'Developed a tracking system for semiconductor manufacturing at NXP using Angular (frontend), C#/.NET (backend), and MSSQL. Built real-time dashboards using Kibana, Elasticsearch (ELK Stack), and Grafana for production data visualization.',
    },
    'predictive-maintenance': {
      title: 'ML-based Predictive Maintenance',
      place: 'NXP Semiconductors',
      summary: 'Machine-learning models that predict when semiconductor equipment will need maintenance.',
      details:
        'Led the FabKey Predictive Maintenance project at NXP using Python, implementing models including Logistic Regression, KNN, Decision Tree, SVM, and Ensemble methods for semiconductor equipment maintenance prediction.',
    },
    'glass-production-tracking': {
      title: 'Glass Production Tracking System',
      place: 'Somchai Aluminium',
      summary:
        'Follows each piece of glass through the production stations by scanning barcodes with a phone camera. Currently in staff testing.',
      details:
        'Operators scan barcodes at each station with a phone camera, and every screen updates in real time over WebSocket. Devices are paired to stations and operators sign in with a PIN. The system is deployed and being tested by staff; it is not in production yet.',
    },
    'pricing-calculator': {
      title: 'Pricing Calculator for Aluminium and Glass Work',
      place: 'Somchai Aluminium',
      summary:
        'Works out the price of a job from templates, profile sections and a bill of materials. Currently in staff testing.',
      details:
        'Calculates the price of aluminium and glass jobs from product templates, profile cross-sections and the bill of materials. The system is deployed and being tested by staff; it is not in production yet.',
    },
    'line-ordering': {
      title: 'LINE Integration for Food Ordering',
      place: 'PH Capital (Pizza Hut)',
      summary: 'Food ordering and support automation through a LINE Official Account.',
      details:
        'Integrated with LINE Official Account for food ordering and support automation via the LINE Messaging API. Created web tools for survey collection, contact reporting, and automated WordPress deployment.',
    },
    'fpga-rl': {
      title: 'FPGA Implementation of Reinforcement Learning',
      place: null,
      summary: 'Q-learning implemented as digital circuits on an FPGA, written in VHDL.',
      details:
        'Designed digital circuits on FPGA for implementing Q-learning algorithms with VHDL, optimizing for hardware performance. Created a testing environment with OpenAI Gym for validating decision problems. Applied reinforcement learning concepts to hardware acceleration.',
    },
  },
  // Newest first.
  experiences: [
    {
      id: 'software-marketplace',
      company: 'Software Marketplace',
      role: 'Chief Technology Officer',
      period: 'Present',
      description:
        'The only engineer at an early-stage software marketplace startup. I own the whole product: architecture, development, deployment and operations.',
      achievements: [],
    },
    {
      id: 'findback',
      company: 'FindBack (findbacktech.com)',
      role: 'Founder',
      period: 'Present',
      description:
        'Own and run FindBack, a free platform for posting and finding lost and found items across Thailand.',
      achievements: [],
    },
    {
      id: 'somchai-sag',
      company: 'Somchai Aluminium Co., Ltd. and SAG All Color Co., Ltd.',
      role: 'Team Lead, Digital Transformation',
      period: 'Present',
      description:
        'Lead digital transformation for an aluminium and glass manufacturer and its powder-coating sister company: I design the internal systems, deploy them and keep them running.',
      achievements: [
        'Put a project and quotation management system into daily use, covering quotations, approvals, invoices, payments and delivery notes in Thai, Chinese and English',
        'Set up one self-hosted server that runs six web applications in Docker behind Nginx and Cloudflare, with nightly backups and a repeatable deploy procedure',
        'Deployed a glass production tracking system and a pricing calculator, both now in staff testing',
        'Moving inventory and purchasing off AppSheet and Google Sheets onto an in-house system',
      ],
    },
    {
      id: 'ph-capital',
      company: 'PH Capital (Pizza Hut)',
      role: 'Senior Software Developer',
      period: 'Feb 2024 to Jul 2025',
      description:
        'Led development of web applications and internal tools for Pizza Hut Thailand operations, and mentored junior developers.',
      achievements: [
        "Built and maintained the 'Central' web platform for internal operations: coupons, store management, employee records, and customer data",
        'Developed the frontend using React, and backend APIs using Express.js with an MSSQL database',
        'Organized backend logic into cleanly separated Controller / Router / Service layers',
        'Integrated with LINE Official Account for food ordering and support automation via the LINE Messaging API',
        'Created web tools for survey collection, contact reporting, and automated WordPress deployment',
      ],
    },
    {
      id: 'nxp',
      company: 'NXP Semiconductors',
      role: 'Software Engineer',
      period: 'Jan 2023 to Jan 2024',
      description:
        'Developed enterprise software solutions for semiconductor manufacturing and testing. Worked with cross-functional teams to implement quality assurance and production monitoring systems.',
      achievements: [
        'Developed a lot status tracking system using Angular (frontend), C#/.NET (backend), and MSSQL',
        'Built real-time dashboards using Kibana, Elasticsearch (ELK Stack), and Grafana for production data visualization',
        'Used Docker to containerize applications and services',
        'Led the ML-based FabKey Predictive Maintenance project using Python: implemented models including Logistic Regression, KNN, Decision Tree, SVM, and Ensemble methods',
      ],
    },
    {
      id: 'western-digital',
      company: 'Western Digital Storage',
      role: 'Software Engineer Intern',
      period: 'May 2022 to Jan 2023',
      description:
        'Contributed to improving image processing algorithms for contaminant detection on hard disk drives. Developed desktop applications for engineers and technicians.',
      achievements: [
        'Created desktop software for detecting microscopic contaminants on HDD media using Python and OpenCV',
        'Reduced false positives via image filtering techniques and enhanced signal isolation',
        'Integrated a C# frontend for user interaction with the detection system',
        'The solution was successfully adopted and filed for patent by the company',
      ],
    },
  ],
  skillGroups: {
    frontend: 'Frontend',
    backend: 'Backend',
    data: 'Data',
    infrastructure: 'Infrastructure',
    ai: 'AI and machine vision',
    hardware: 'Hardware',
    markets: 'Trading tools',
  },
  markets: {
    title: 'Trading and markets',
    lead: 'I trade CFDs and equities, and I write my own tools for it.',
    points: [
      { term: 'Price action', text: 'I read the chart itself, with pin bars as my main signal.' },
      {
        term: 'Market microstructure',
        text: 'How liquidity and order flow move price, including stop hunts and liquidity sweeps.',
      },
      { term: 'Macro context', text: 'Economics and politics, and how they set the direction of a market.' },
      { term: 'Automation', text: 'Trading scripts and indicators for MetaTrader 5, written in MQL5.' },
    ],
  },
  education: [
    {
      school: "King Mongkut's Institute of Technology Ladkrabang",
      detail: 'Bachelor of Engineering in Telecommunication Engineering',
      period: '2019 to 2023',
    },
    {
      school: "King Mongkut's Institute of Technology Ladkrabang",
      detail: 'Minor program in AI and Deep Learning',
      period: '2019 to 2023',
    },
    {
      school: 'Benchamaracharungsarit School',
      detail: 'Mathematics and Science Program',
      period: '2016 to 2019',
    },
  ],
  activities: [
    'Vice Chairman of Students, Department of Telecommunications, KMITL, 2019 to 2023',
    'Tutor at the Physics and Mathematical Academy, KMITL, 2019 to 2021',
    'Studied ICT at Huawei ICT Academy',
    'Took part in DDCamp 5, on site',
  ],
  interests: [
    'AIAT MOOC and Super AI Engineering season 2',
    'KMITL Football Club Academy',
    'Swimming, football, basketball and Thai boxing',
  ],
};

export default en;
